import debounce from 'p-debounce';
import { onUnmounted } from 'vue';

import logger from '@/logger';

const log = logger.getChild('debounced-retry');

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyFunction = (...arguments_: readonly any[]) => Promise<unknown>;

const exponentialDelay = (attempt: number, maxDelay = 15_000) => {
  return Math.min(maxDelay, Math.pow(2, attempt) * 1000);
};

/**
 * Create function wrapper that retries a function on exception with exponential backoff until it executes successfully
 * @param fn - function to call
 * @returns function wrapper and cancellation callback
 */
export const exponentialRetry = <F extends AnyFunction>(
  fn: F,
): { withRetry: F; cancel: () => void } => {
  const timers = new Set<number>();

  const withRetryCounter = async (
    ...args: Parameters<F>
  ): Promise<ReturnType<F>> => {
    let attempt = 0;
    log.debug('exponentialRetry initiated');

    const wrapper = async (): Promise<ReturnType<F>> => {
      try {
        const result = (await fn(...args)) as ReturnType<F>;
        log.debug('exponentialRetry finished');

        return result;
      } catch {
        log.debug('exponentialRetry failed on attempt:', attempt);
        attempt++;
        const delay = exponentialDelay(attempt);
        await new Promise<void>((resolve) => {
          const t = setTimeout(() => {
            resolve();
            timers.delete(t);
          }, delay) as unknown as number;
          timers.add(t);
        });
        return wrapper();
      }
    };

    return wrapper();
  };

  return {
    withRetry: withRetryCounter as F,
    cancel: () => {
      for (const t of timers) {
        clearTimeout(t);
      }
    },
  };
};

export const debouncedExponentialRetry = <F extends AnyFunction>(
  fn: F,
  debounceDelay = 0,
): {
  debounced: F;
  cancel: () => void;
} => {
  let timer: number | null = null;
  let attempt = 0;

  const wrapper = async (...args: Parameters<F>): Promise<ReturnType<F>> => {
    try {
      const result = (await fn(...args)) as ReturnType<F>;
      attempt = 0;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      return result;
    } catch {
      attempt += 1;
      const delay = exponentialDelay(attempt);
      await new Promise((resolve) => {
        timer = setTimeout(resolve, delay) as unknown as number;
      });
      return wrapper(...args);
    }
  };
  const debounced = debounce(wrapper, debounceDelay) as F;

  return {
    debounced,
    cancel: () => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
  };
};

export const useDebouncedExponentialRetry = <F extends AnyFunction>(
  fn: F,
  debounceDelay = 0,
): F => {
  const { debounced, cancel } = debouncedExponentialRetry(fn, debounceDelay);
  onUnmounted(cancel);

  return debounced;
};
